import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FolderOpen, Search, RefreshCw, Play, Eye, Trash2, 
  Beaker, CheckCircle2, Clock, XCircle, AlertTriangle,
  User, Calendar, Tag, TestTube2, Loader2, CloudUpload,
  Plus, Package, ArrowUpRight, Filter, Edit, Save, X
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";

// Test Status Values (as per requirement)
const TEST_STATUS_OPTIONS = ["Not Started", "Started", "End", "Complete", "Finish"];

const STATUS_COLORS = {
  "Not Started": "bg-slate-100 text-slate-700",
  "Started": "bg-blue-100 text-blue-700",
  "End": "bg-amber-100 text-amber-700",
  "Complete": "bg-emerald-100 text-emerald-700",
  "Finish": "bg-vcc-teal/20 text-vcc-teal",
  "In Progress": "bg-blue-100 text-blue-700",
  "Testing": "bg-yellow-100 text-yellow-700",
  "Completed": "bg-green-100 text-green-700"
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
};

export default function ProductFoldersPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [stats, setStats] = useState({});
  
  // Delete confirmation
  const [deleteModal, setDeleteModal] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Quick Edit state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [saving, setSaving] = useState(false);

  // Open quick edit dialog
  const openEditDialog = (folder) => {
    setEditingFolder({ ...folder });
    setShowEditDialog(true);
  };

  // Handle edit field change
  const handleEditChange = (field, value) => {
    setEditingFolder(prev => ({ ...prev, [field]: value }));
  };

  // Save edited folder
  const handleSaveEdit = async () => {
    if (!editingFolder) return;
    
    setSaving(true);
    try {
      const response = await axios.put(`${API}/product-folders/${editingFolder.id}`, editingFolder);
      
      if (response.data.success) {
        toast.success("Product folder updated successfully!");
        if (response.data.zohoSync?.success) {
          toast.success("✅ Synced to Zoho Creator");
        }
        setShowEditDialog(false);
        setEditingFolder(null);
        fetchFolders();
      } else {
        toast.error(response.data.error || "Failed to update folder");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update folder");
    } finally {
      setSaving(false);
    }
  };

  const fetchFolders = useCallback(async () => {
    try {
      const [foldersRes, statsRes] = await Promise.all([
        axios.get(`${API}/product-folders`, { params: { userId: user?.id, role: user?.role } }),
        axios.get(`${API}/dashboard/product-stats`)
      ]);
      
      if (foldersRes.data.success) {
        setFolders(foldersRes.data.folders || []);
      }
      if (statsRes.data.success) {
        setStats(statsRes.data.stats || {});
      }
    } catch (error) {
      console.error("Failed to fetch folders:", error);
      toast.error("Failed to load product folders");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  const handleSyncFromZoho = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/product-folders/sync-from-rd`);
      if (response.data.success) {
        toast.success(`Synced ${response.data.created || 0} new products, updated ${response.data.updated || 0}`);
        fetchFolders();
      } else {
        // Check for API limit error
        if (response.data.limitReached || (response.data.error && response.data.error.includes('API limit'))) {
          toast.error("Zoho API limit reached. Please try again later or upgrade your Zoho plan.", {
            duration: 5000
          });
        } else {
          toast.error(response.data.error || response.data.message || "Sync failed");
        }
      }
    } catch (error) {
      console.error("Sync error:", error);
      const errorMsg = error.response?.data?.error || error.response?.data?.detail || "Failed to sync from Zoho";
      if (errorMsg.includes('API limit')) {
        toast.error("Zoho API limit reached. Please try again later or upgrade your Zoho plan.", {
          duration: 5000
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleStartDevelopment = async (folderId) => {
    try {
      const response = await axios.post(`${API}/product-folders/${folderId}/start-development`, {
        userId: user?.id,
        userName: user?.name
      });
      if (response.data.success) {
        toast.success("Development started! First iteration created.");
        fetchFolders();
        navigate(`/products/${folderId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start development");
    }
  };

  const handleDeleteClick = (folder) => {
    setFolderToDelete(folder);
    setDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!folderToDelete) return;
    setDeleting(true);
    try {
      const response = await axios.delete(`${API}/product-folders/${folderToDelete.id}`);
      if (response.data.success) {
        toast.success("Product folder deleted");
        setFolders(folders.filter(f => f.id !== folderToDelete.id));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete folder");
    } finally {
      setDeleting(false);
      setDeleteModal(false);
      setFolderToDelete(null);
    }
  };

  // Filter folders
  const filteredFolders = folders.filter(folder => {
    const matchesSearch = 
      folder.productName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      folder.sampleNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      folder.clientName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || folder.testStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Loader2 className="w-8 h-8 text-vcc-teal" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8" data-testid="product-folders-page">
      {/* Header */}
      <motion.div 
        className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div>
          <h1 className="text-3xl font-poppins font-bold text-slate-800">Product Development</h1>
          <p className="text-slate-500 mt-1 font-poppins">Manage your R&D product folders and formulations</p>
        </div>
        
        <motion.button
          onClick={handleSyncFromZoho}
          disabled={syncing}
          className="flex items-center gap-2 px-5 py-2.5 bg-vcc-teal text-white rounded-xl font-poppins font-medium shadow-lg shadow-vcc-teal/20 hover:shadow-vcc-teal/40 transition-all disabled:opacity-50"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="sync-btn"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing...' : 'Sync from Zoho'}
        </motion.button>
      </motion.div>

      {/* Stats Cards */}
      <motion.div 
        className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="backdrop-blur-xl bg-white/90 border border-vcc-pink-100 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-vcc-teal/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-vcc-teal" />
            </div>
            <div>
              <p className="text-2xl font-poppins font-bold text-slate-800">{stats.total || 0}</p>
              <p className="text-xs text-slate-500 font-poppins">Total Products</p>
            </div>
          </div>
        </div>
        
        <div className="backdrop-blur-xl bg-white/90 border border-vcc-pink-100 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-poppins font-bold text-slate-800">{stats.inProgress || 0}</p>
              <p className="text-xs text-slate-500 font-poppins">In Progress</p>
            </div>
          </div>
        </div>
        
        <div className="backdrop-blur-xl bg-white/90 border border-vcc-pink-100 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <TestTube2 className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-poppins font-bold text-slate-800">{stats.testing || 0}</p>
              <p className="text-xs text-slate-500 font-poppins">Testing</p>
            </div>
          </div>
        </div>
        
        <div className="backdrop-blur-xl bg-white/90 border border-vcc-pink-100 rounded-2xl p-5 shadow-lg">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-2xl font-poppins font-bold text-slate-800">{stats.completed || 0}</p>
              <p className="text-xs text-slate-500 font-poppins">Completed</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Search & Filters */}
      <motion.div 
        className="flex flex-col sm:flex-row gap-4 mb-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by product name, sample no, or client..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white/80 border-vcc-pink-100 focus:border-vcc-teal font-poppins"
            data-testid="search-input"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] bg-white/80 border-vcc-pink-100 font-poppins">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {TEST_STATUS_OPTIONS.map(status => (
              <SelectItem key={status} value={status}>{status}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </motion.div>

      {/* Products Grid */}
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <AnimatePresence>
          {filteredFolders.length === 0 ? (
            <motion.div 
              variants={itemVariants}
              className="col-span-full py-16 text-center"
            >
              <FolderOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-poppins font-semibold text-slate-600 mb-2">No Products Found</h3>
              <p className="text-slate-500 font-poppins">Try adjusting your search or sync from Zoho</p>
            </motion.div>
          ) : (
            filteredFolders.map((folder, index) => (
              <motion.div
                key={folder.id}
                variants={itemVariants}
                layout
                className="backdrop-blur-xl bg-white/90 border border-vcc-pink-100 rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all group"
                data-testid={`folder-card-${folder.id}`}
              >
                {/* Card Header */}
                <div className="p-5 border-b border-vcc-pink-50">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-vcc-teal/10 flex items-center justify-center">
                        <Beaker className="w-5 h-5 text-vcc-teal" />
                      </div>
                      <div>
                        <h3 className="font-poppins font-semibold text-slate-800 line-clamp-1">{folder.productName}</h3>
                        <p className="text-sm text-slate-500 font-poppins">{folder.sampleNo}</p>
                      </div>
                    </div>
                    <Badge className={`${STATUS_COLORS[folder.testStatus] || STATUS_COLORS["Not Started"]} font-poppins text-xs`}>
                      {folder.testStatus || "Not Started"}
                    </Badge>
                  </div>
                  
                  {/* Client & SKU */}
                  <div className="flex flex-wrap gap-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1 font-poppins">
                      <User className="w-3 h-3" /> {folder.clientName || "N/A"}
                    </span>
                    {folder.sku && (
                      <span className="flex items-center gap-1 font-poppins">
                        <Tag className="w-3 h-3" /> {folder.sku}
                      </span>
                    )}
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-5 bg-vcc-cream-50/30">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-slate-600 font-poppins">
                      {folder.iterations?.length || 0} Iteration{(folder.iterations?.length || 0) !== 1 ? 's' : ''}
                    </span>
                    <span className="text-sm text-slate-600 font-poppins">
                      {folder.assignedLabAssistant || "Unassigned"}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    {folder.iterations?.length > 0 ? (
                      <Button
                        onClick={() => navigate(`/products/${folder.id}`)}
                        className="flex-1 bg-vcc-teal hover:bg-vcc-teal/90 text-white font-poppins"
                        size="sm"
                        data-testid={`view-btn-${folder.id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" /> View Details
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleStartDevelopment(folder.id)}
                        className="flex-1 bg-vcc-teal hover:bg-vcc-teal/90 text-white font-poppins"
                        size="sm"
                        data-testid={`start-btn-${folder.id}`}
                      >
                        <Play className="w-4 h-4 mr-2" /> Start Development
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      onClick={() => openEditDialog(folder)}
                      style={{ backgroundColor: '#2563eb', color: 'white' }}
                      className="hover:opacity-90 shadow-md"
                      data-testid={`edit-btn-${folder.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(folder)}
                      className="border-red-200 text-red-500 hover:bg-red-50"
                      data-testid={`delete-btn-${folder.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </AnimatePresence>
      </motion.div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModal} onOpenChange={setDeleteModal}>
        <DialogContent className="bg-white border-vcc-pink-100">
          <DialogHeader>
            <DialogTitle className="font-poppins text-slate-800">Delete Product Folder?</DialogTitle>
            <DialogDescription className="font-poppins text-slate-500">
              Are you sure you want to delete <strong>{folderToDelete?.productName}</strong>? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModal(false)} className="font-poppins">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={deleting}
              className="font-poppins"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Edit Product Folder Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-poppins text-xl flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Product - {editingFolder?.productName}
            </DialogTitle>
          </DialogHeader>

          {editingFolder && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Sample No</Label>
                  <Input value={editingFolder.sampleNo || ""} disabled className="mt-1 bg-slate-50" />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Product Name</Label>
                  <Input 
                    value={editingFolder.productName || ""} 
                    onChange={(e) => handleEditChange("productName", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Client Name</Label>
                  <Input 
                    value={editingFolder.clientName || ""} 
                    onChange={(e) => handleEditChange("clientName", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">SKU</Label>
                  <Input 
                    value={editingFolder.sku || ""} 
                    onChange={(e) => handleEditChange("sku", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Test Status</Label>
                  <Select 
                    value={editingFolder.testStatus || "Not Started"} 
                    onValueChange={(value) => handleEditChange("testStatus", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEST_STATUS_OPTIONS.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Test Priority</Label>
                  <Select 
                    value={editingFolder.testPriority || "Medium"} 
                    onValueChange={(value) => handleEditChange("testPriority", value)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">Low</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Assigned Lab Assistant</Label>
                  <Input 
                    value={editingFolder.assignedLabAssistant || ""} 
                    onChange={(e) => handleEditChange("assignedLabAssistant", e.target.value)}
                    className="mt-1" 
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-600 font-poppins">Packaging Preference</Label>
                  <Input 
                    value={editingFolder.packagingPreference || ""} 
                    onChange={(e) => handleEditChange("packagingPreference", e.target.value)}
                    className="mt-1" 
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowEditDialog(false)}
              className="font-poppins"
            >
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              style={{ backgroundColor: '#2563eb', color: 'white' }}
              className="hover:opacity-90 font-poppins shadow-md"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save & Sync to Zoho
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
